import { FC, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import BrochureDetailView from './BrochureDetailView';
import BrochureConfiguratorView from './BrochureConfiguratorView';
import BrochureOrderView from './BrochureOrderView';
import { Route, ViewName } from './types';
import { BrochureBasketDetail, BrochureDetail, getBrochureById } from '../../api/brochures';
import LoadingModal from '../../components/LoadingModal/LoadingModal';

const parseHash = (): Route => {
    const hash = window.location.hash.slice(1);
    if (!hash) return { view: 'detail', id: null };
    const dashIdx = hash.indexOf('-');
    if (dashIdx === -1) return { view: 'detail', id: null };
    const view = hash.slice(0, dashIdx) as ViewName;
    const id = hash.slice(dashIdx + 1);
    return { view, id };
};

const applyRoute = (route: Route): void => {
    if (route.view === 'detail') {
        history.pushState('', document.title, window.location.pathname + window.location.search);
    } else {
        history.pushState(
            '',
            document.title,
            window.location.pathname + window.location.search + `#${route.view}-${route.id}`,
        );
    }
};

const BrochureDetailPage: FC = () => {
    const basketId = new URLSearchParams(window.location.search).get('id') ?? '';

    const [routeHistory, setRouteHistory] = useState<Route[]>(() => [parseHash()]);
    const currentRoute = routeHistory[routeHistory.length - 1];
    const [basketData, setBasketData] = useState<BrochureBasketDetail | null>(null);
    const [brochureData, setBrochureData] = useState<BrochureDetail | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const handleHashChange = () => {
            setRouteHistory([parseHash()]);
        };
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    useEffect(() => {
        if (currentRoute.view === 'configurator' && currentRoute.id && currentRoute.id !== 'new') {
            setIsLoading(true);
            getBrochureById(basketId, currentRoute.id)
                .then((result) => setBrochureData(result))
                .catch(() => setBrochureData(null))
                .finally(() => setIsLoading(false));
        } else {
            setBrochureData(null);
        }
    }, [currentRoute.view, currentRoute.id]);

    const navigateTo = (route: Route) => {
        applyRoute(route);
        setRouteHistory((prev) => [...prev, route]);
    };

    const navigateBack = (needsWarning: boolean = false) => {
        if (routeHistory.length <= 1) {
            applyRoute({ view: 'detail', id: null });
            setRouteHistory([{ view: 'detail', id: null }]);
            return;
        }
        const newHistory = routeHistory.slice(0, -1);
        const prevRoute = newHistory[newHistory.length - 1];
        applyRoute(prevRoute);
        setRouteHistory(newHistory);
    };

    const renderView = () => {
        if (currentRoute.view === 'configurator' && currentRoute.id) {
            return (
                <BrochureConfiguratorView
                    basketId={basketId}
                    brochureId={currentRoute.id}
                    brochureData={brochureData}
                    products={basketData?.products ?? []}
                    raccoltaBrochure={basketData?.name ?? ''}
                    company={basketData?.company ?? ''}
                    onLoadingChange={setIsLoading}
                    onNavigate={navigateTo}
                />
            );
        }

        if (currentRoute.view === 'create_order' && currentRoute.id) {
            return <BrochureOrderView basketId={basketId} brochureId={currentRoute.id} onBack={navigateBack} />;
        }

        return (
            <BrochureDetailView
                basketId={basketId}
                data={basketData}
                onDataChange={setBasketData}
                onLoadingChange={setIsLoading}
                onNavigate={navigateTo}
            />
        );
    };

    return (
        <div className="brochureDetailsPageRoot">
            <LoadingModal isVisible={isLoading} />
            {renderView()}
        </div>
    );
};

const BrochureDetailPageEl = document.getElementById('BrochureDetailsPage');
const root = BrochureDetailPageEl && createRoot(BrochureDetailPageEl as HTMLElement);
root && root.render(<BrochureDetailPage />);
